// Component: ModelPerformanceCharts.jsx
// Purpose: Read-only, single-scale SVG charts rendered from one stored model scorecard.

import React from "react";

const WIDTH = 640;
const HEIGHT = 220;
const PADDING = { top: 16, right: 18, bottom: 38, left: 58 };
const MODEL_COLOR = "var(--theme-purple, #7352ff)";
const BASELINE_COLOR = "var(--color-warning, #d97706)";
const GRID_COLOR = "var(--divider, #d1d5db)";
const TEXT_COLOR = "var(--color-fg-3, #64748b)";

function finiteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function monthLabel(row, index) {
  const raw = row?.month;
  return raw ? String(raw).slice(0, 7) : `OOS ${index + 1}`;
}

/**
 * Normalize the one stored scorecard.performance contract emitted by training.
 *
 * @param {object|null} scorecard Stored scorecard returned by GET versions/{id}.
 * @returns {object} Chart-ready monthly, reliability, skill, and confusion data.
 */
export function normalizePerformanceScorecard(scorecard) {
  const performance = scorecard?.performance || {};
  const rawMonths = performance.oos_months || [];
  const months = Array.isArray(rawMonths) ? rawMonths.map((row, index) => {
    const modelPnl = finiteNumber(row?.model_pnl);
    const baselinePnl = finiteNumber(row?.baseline_pnl);
    return {
      label: monthLabel(row, index),
      modelPnl,
      baselinePnl,
      modelCumulativePnl: finiteNumber(row?.model_cum_pnl),
      baselineCumulativePnl: finiteNumber(row?.baseline_cum_pnl),
      balancedAccuracy: finiteNumber(row?.balanced_accuracy),
      baselineBalancedAccuracy: finiteNumber(row?.baseline_accuracy),
    };
  }) : [];

  const rawReliability = performance.reliability || [];
  const reliability = Array.isArray(rawReliability) ? rawReliability.map((bin) => ({
    predicted: finiteNumber(bin?.p_pred),
    observed: finiteNumber(bin?.p_obs),
    count: finiteNumber(bin?.n),
  })).filter((bin) => bin.predicted !== null && bin.observed !== null) : [];

  const rawConfusion = performance.confusion;
  const matrix = rawConfusion?.matrix || [];
  const defaultLabels = matrix.length === 3 ? ["down", "flat", "up"] : matrix.map((_, index) => String(index));
  const labels = rawConfusion?.labels || defaultLabels;

  const monthlySkill = months.filter((row) => (
    row.balancedAccuracy !== null || row.baselineBalancedAccuracy !== null
  ));

  return {
    months,
    reliability,
    confusion: {
      labels: Array.isArray(labels) ? labels.map(String) : [],
      matrix: Array.isArray(matrix) ? matrix : [],
    },
    skill: monthlySkill,
  };
}

function formatNumber(value, percent = false) {
  if (value === null || value === undefined) return "—";
  return percent ? `${(value * 100).toFixed(1)}%` : value.toFixed(2);
}

function chartScale(values, { includeZero = false, percent = false } = {}) {
  const present = values.filter((value) => value !== null && Number.isFinite(value));
  if (present.length === 0) return null;
  let min = Math.min(...present);
  let max = Math.max(...present);
  if (includeZero) {
    min = Math.min(0, min);
    max = Math.max(0, max);
  }
  if (percent) {
    min = Math.min(0, min);
    max = Math.max(1, max);
  }
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    min -= pad;
    max += pad;
  }
  const innerHeight = HEIGHT - PADDING.top - PADDING.bottom;
  return {
    min,
    max,
    y: (value) => PADDING.top + ((max - value) / (max - min)) * innerHeight,
  };
}

function Axis({ scale, labels, percent = false }) {
  const innerWidth = WIDTH - PADDING.left - PADDING.right;
  const ticks = [scale.max, (scale.max + scale.min) / 2, scale.min];
  return (
    <>
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={PADDING.left} x2={WIDTH - PADDING.right} y1={scale.y(tick)} y2={scale.y(tick)} stroke={GRID_COLOR} strokeWidth="1" />
          <text x={PADDING.left - 8} y={scale.y(tick) + 4} textAnchor="end" fontSize="10" fill={TEXT_COLOR}>
            {formatNumber(tick, percent)}
          </text>
        </g>
      ))}
      <line x1={PADDING.left} x2={PADDING.left} y1={PADDING.top} y2={HEIGHT - PADDING.bottom} stroke={TEXT_COLOR} />
      <line x1={PADDING.left} x2={WIDTH - PADDING.right} y1={HEIGHT - PADDING.bottom} y2={HEIGHT - PADDING.bottom} stroke={TEXT_COLOR} />
      {labels.map((label, index) => {
        const x = labels.length === 1
          ? PADDING.left + innerWidth / 2
          : PADDING.left + (index / (labels.length - 1)) * innerWidth;
        return (
          <text key={`${label}-${index}`} x={x} y={HEIGHT - 16} textAnchor="middle" fontSize="10" fill={TEXT_COLOR}>
            {label}
          </text>
        );
      })}
    </>
  );
}

function Legend({ items }) {
  return (
    <div className="mb-2 flex flex-wrap gap-4 text-xs text-fg-3" aria-label="Chart legend">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} aria-hidden="true" />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <section className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
      <h4 className="mb-3 text-sm font-semibold text-fg-1">{title}</h4>
      {children}
    </section>
  );
}

function MissingChart({ title }) {
  return (
    <ChartCard title={title}>
      <p className="py-12 text-center text-sm text-fg-3">Not in scorecard</p>
    </ChartCard>
  );
}

function LineChart({ ariaLabel, labels, series, percent = false, includeZero = false }) {
  const scale = chartScale(series.flatMap((entry) => entry.values), { includeZero, percent });
  const innerWidth = WIDTH - PADDING.left - PADDING.right;
  if (!scale) return null;
  const x = (index) => labels.length === 1
    ? PADDING.left + innerWidth / 2
    : PADDING.left + (index / (labels.length - 1)) * innerWidth;
  return (
    <>
      {series.length >= 2 && <Legend items={series} />}
      <svg role="img" aria-label={ariaLabel} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-56 w-full text-fg-3">
        <Axis scale={scale} labels={labels} percent={percent} />
        {series.map((entry) => {
          const points = entry.values
            .map((value, index) => value === null ? null : `${x(index)},${scale.y(value)}`)
            .filter(Boolean)
            .join(" ");
          return (
            <g key={entry.label}>
              {points && <polyline points={points} fill="none" stroke={entry.color} strokeWidth="3" />}
              {entry.values.map((value, index) => value === null ? null : (
                <circle key={`${entry.label}-${labels[index]}`} cx={x(index)} cy={scale.y(value)} r="5" fill={entry.color}>
                  <title>{entry.tooltip(labels[index], value, index)}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </>
  );
}

function BarChart({ labels, modelValues, baselineValues }) {
  const scale = chartScale([...modelValues, ...baselineValues], { includeZero: true });
  if (!scale) return null;
  const innerWidth = WIDTH - PADDING.left - PADDING.right;
  const groupWidth = innerWidth / labels.length;
  const barWidth = Math.min(24, groupWidth * 0.3);
  const zeroY = scale.y(0);
  return (
    <>
      <Legend items={[{ label: "Model", color: MODEL_COLOR }, { label: "Baseline", color: BASELINE_COLOR }]} />
      <svg role="img" aria-label="Per-month P&L bars for model and baseline" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-56 w-full text-fg-3">
        <Axis scale={scale} labels={labels} />
        {labels.map((label, index) => {
          const center = PADDING.left + groupWidth * (index + 0.5);
          return [
            { name: "Model", value: modelValues[index], x: center - barWidth - 2, color: MODEL_COLOR },
            { name: "Baseline", value: baselineValues[index], x: center + 2, color: BASELINE_COLOR },
          ].map((bar) => {
            if (bar.value === null) return null;
            const valueY = scale.y(bar.value);
            return (
              <rect key={`${bar.name}-${label}`} x={bar.x} y={Math.min(zeroY, valueY)} width={barWidth} height={Math.max(2, Math.abs(valueY - zeroY))} rx="2" fill={bar.color}>
                <title>{`${label} · ${bar.name} P&L: ${formatNumber(bar.value)}`}</title>
              </rect>
            );
          });
        })}
      </svg>
    </>
  );
}

function ConfusionTable({ confusion }) {
  const { labels, matrix } = confusion;
  const valid = labels.length > 0 && matrix.length === labels.length
    && matrix.every((row) => Array.isArray(row) && row.length === labels.length);
  if (!valid) return null;
  const max = Math.max(1, ...matrix.flat().map((value) => finiteNumber(value) ?? 0));
  return (
    <div className="overflow-x-auto">
      <table aria-label="Confusion summary, actual rows and predicted columns" className="w-full border-collapse text-center text-xs text-fg-2">
        <thead>
          <tr>
            <th className="p-2 text-left text-fg-3">Actual ↓ / Predicted →</th>
            {labels.map((label) => <th key={label} className="p-2 capitalize">{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, rowIndex) => (
            <tr key={labels[rowIndex]}>
              <th className="p-2 text-left capitalize">{labels[rowIndex]}</th>
              {row.map((value, columnIndex) => {
                const count = finiteNumber(value) ?? 0;
                return (
                  <td
                    key={`${rowIndex}-${columnIndex}`}
                    className="border border-[var(--divider)] p-3 font-mono tabular-nums"
                    style={{ backgroundColor: `color-mix(in srgb, ${MODEL_COLOR} ${Math.round((count / max) * 45)}%, transparent)` }}
                    title={`Actual ${labels[rowIndex]}, predicted ${labels[columnIndex]}: ${count}`}
                  >
                    {count}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Render the approved Gate-2 scorecard visualizations for the selected version.
 *
 * @param {{scorecard: object|null}} props Component props.
 * @returns {React.ReactElement} Five read-only chart cards with missing-data states.
 */
export default function ModelPerformanceCharts({ scorecard }) {
  const data = normalizePerformanceScorecard(scorecard);
  const pnlMonths = data.months.filter((row) => row.modelPnl !== null || row.baselinePnl !== null);
  const cumulativeMonths = data.months.filter((row) => (
    row.modelCumulativePnl !== null || row.baselineCumulativePnl !== null
  ));
  const hasConfusion = data.confusion.labels.length > 0
    && data.confusion.matrix.length === data.confusion.labels.length
    && data.confusion.matrix.every((row) => (
      Array.isArray(row) && row.length === data.confusion.labels.length
    ));

  return (
    <section aria-labelledby="performance-charts-heading" className="space-y-4">
      <h3 id="performance-charts-heading" className="text-base font-semibold text-fg-1">OOS performance charts</h3>
      <div className="grid gap-4 xl:grid-cols-2">
        {cumulativeMonths.length === 0 ? <MissingChart title="Cumulative P&L" /> : (
          <ChartCard title="Cumulative P&L">
            <LineChart
              ariaLabel="Cumulative P&L of model and baseline across OOS months"
              labels={cumulativeMonths.map((row) => row.label)}
              includeZero
              series={[
                {
                  label: "Model",
                  color: MODEL_COLOR,
                  values: cumulativeMonths.map((row) => row.modelCumulativePnl),
                  tooltip: (label, value) => `${label} · Model cumulative P&L: ${formatNumber(value)}`,
                },
                {
                  label: "Baseline",
                  color: BASELINE_COLOR,
                  values: cumulativeMonths.map((row) => row.baselineCumulativePnl),
                  tooltip: (label, value) => `${label} · Baseline cumulative P&L: ${formatNumber(value)}`,
                },
              ]}
            />
          </ChartCard>
        )}

        {pnlMonths.length === 0 ? <MissingChart title="Per-month P&L" /> : (
          <ChartCard title="Per-month P&L">
            <BarChart
              labels={pnlMonths.map((row) => row.label)}
              modelValues={pnlMonths.map((row) => row.modelPnl)}
              baselineValues={pnlMonths.map((row) => row.baselinePnl)}
            />
          </ChartCard>
        )}

        {data.skill.length === 0 ? <MissingChart title="Skill vs baseline" /> : (
          <ChartCard title="Skill vs baseline">
            <LineChart
              ariaLabel="Skill vs baseline using balanced accuracy"
              labels={data.skill.map((row) => row.label)}
              percent
              series={[
                {
                  label: "Model",
                  color: MODEL_COLOR,
                  values: data.skill.map((row) => row.balancedAccuracy),
                  tooltip: (label, value) => `${label} · Model balanced accuracy: ${formatNumber(value, true)}`,
                },
                {
                  label: "Baseline",
                  color: BASELINE_COLOR,
                  values: data.skill.map((row) => row.baselineBalancedAccuracy),
                  tooltip: (label, value) => `${label} · Baseline balanced accuracy: ${formatNumber(value, true)}`,
                },
              ]}
            />
          </ChartCard>
        )}

        {data.reliability.length === 0 ? <MissingChart title="Calibration / reliability" /> : (
          <ChartCard title="Calibration / reliability">
            <LineChart
              ariaLabel="Calibration reliability, observed frequency versus predicted probability"
              labels={data.reliability.map((bin) => formatNumber(bin.predicted, true))}
              percent
              series={[
                {
                  label: "Observed",
                  color: MODEL_COLOR,
                  values: data.reliability.map((bin) => bin.observed),
                  tooltip: (_, value, index) => {
                    const bin = data.reliability[index];
                    return `Predicted ${formatNumber(bin.predicted, true)} · Observed ${formatNumber(value, true)}${bin.count === null ? "" : ` · n=${bin.count}`}`;
                  },
                },
                {
                  label: "Perfect calibration",
                  color: BASELINE_COLOR,
                  values: data.reliability.map((bin) => bin.predicted),
                  tooltip: (label) => `Perfect calibration at ${label}`,
                },
              ]}
            />
          </ChartCard>
        )}

        {!hasConfusion ? <MissingChart title="Confusion summary" /> : (
          <ChartCard title="Confusion summary">
            <ConfusionTable confusion={data.confusion} />
          </ChartCard>
        )}
      </div>
    </section>
  );
}
